import { Form, Input, Button, message, Modal, Alert } from 'antd';
import { useState, useEffect } from 'react';
import { WarningOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { sendDeleteAccountCode, deleteAccount } from '@/api/developer';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { useAuthStore } from '@/stores/authStore';
import type { DeleteAccountRequest } from '@/types/auth';

// ✅ 定义表单值类型
interface DeleteAccountFormValues {
  code: string;
  password: string;
  confirm: string;
}

export const DeleteAccountForm = () => {
  const [form] = Form.useForm<DeleteAccountFormValues>();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [sendingCode, setSendingCode] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number>(0);
  const [targetVerified, setTargetVerified] = useState<boolean>(false);
  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  const navigate = useNavigate();
  const { currentDeveloper, logout } = useAuthStore();

  // 倒计时逻辑
  useEffect(() => {
    let timer: number | null = null;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [countdown]);

  // 初始化时确定验证渠道
  useEffect(() => {
    if (currentDeveloper) {
      if (currentDeveloper.email && currentDeveloper.email.trim() !== '') {
        setChannel('email');
      } else if (currentDeveloper.phone && currentDeveloper.phone.trim() !== '') {
        setChannel('sms');
      }
    }
  }, [currentDeveloper]);

  const handleSendCode = async (): Promise<void> => {
    try {
      setSendingCode(true);
      await sendDeleteAccountCode();
      
      message.success(`验证码已发送至${channel === 'email' ? '邮箱' : '手机'}`);
      setCountdown(60);
      setTargetVerified(true);
    } catch (err) {
      if (err instanceof Error) {
        message.error(err.message);
      } else {
        message.error('发送验证码失败');
      }
    } finally {
      setSendingCode(false);
    }
  };

  const onFinish = async (values: DeleteAccountFormValues): Promise<void> => {
    if (!targetVerified) {
      message.warning('请先获取验证码');
      return;
    }

    if (values.confirm !== 'DELETE') {
      message.error('确认字段必须输入 DELETE');
      return;
    }

    Modal.confirm({
      title: '⚠️ 确认注销账号',
      content: (
        <div className="space-y-2">
          <p>注销后以下数据将<strong>永久删除</strong>且无法恢复：</p>
          <ul className="list-disc list-inside text-sm text-gray-600">
            <li>开发者账号信息</li>
            <li>所有 App 及密钥</li>
            <li>所有产品及配置</li>
            <li>所有面板及资源</li>
            <li>所有上传的图片</li>
          </ul>
          <p className="text-red-500 font-medium mt-2">此操作不可撤销！</p>
        </div>
      ),
      okText: '确认注销',
      okButtonProps: { danger: true, loading: submitting },
      cancelText: '取消',
      onOk: async () => {
        setSubmitting(true);
        setError(null);

        try {
          const deleteData: DeleteAccountRequest = {
            target: channel === 'email' && currentDeveloper?.email 
              ? currentDeveloper.email 
              : currentDeveloper?.phone || '',
            channel: channel,
            code: values.code,
            password: values.password,
            confirm: 'DELETE',
          };

          await deleteAccount(deleteData);
          
          logout();
          
          message.success('账号已注销，感谢使用');
          navigate('/login');
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : '账号注销失败';
          setError(errorMessage);
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  const getTarget = () => {
    if (!currentDeveloper) return '';
    if (channel === 'email' && currentDeveloper.email) return currentDeveloper.email;
    if (channel === 'sms' && currentDeveloper.phone) return currentDeveloper.phone;
    return '';
  };

  return (
    <div className="w-full">
      <Alert
        message="危险操作"
        description="注销账号后，所有数据将永久删除且无法恢复。请确保已备份重要数据。"
        type="warning"
        showIcon
        className="mb-6"
        icon={<WarningOutlined className="text-yellow-500" />}
      />

      {error && (
        <div className="mb-6">
          <ErrorAlert message="注销失败" description={error} />
        </div>
      )}

      <br />

      <Form
        form={form}
        name="deleteAccount"
        layout="vertical"
        onFinish={onFinish}
        requiredMark={false}
        className="space-y-6"
        autoComplete="off"  // ✅ 关键：在 Form 级别禁用自动填充
      >
        {/* 验证目标显示 */}
        <Form.Item label={<span className="font-medium text-gray-700">验证{channel === 'email' ? '邮箱' : '手机号'}</span>}>
          <Input value={getTarget()} disabled className="bg-gray-50" readOnly />
        </Form.Item>

        {/* 验证码输入框 + 发送按钮 */}
        <Form.Item
          name="code"
          label={<span className="font-medium text-gray-700 flex items-center">验证码<span className="text-red-500 ml-1">*</span></span>}
          rules={[
            { required: true, message: '请输入验证码' },
            { len: 6, message: '验证码为 6 位数字' },
          ]}
        >
          <div className="flex gap-2">
            <Input 
              placeholder="请输入 6 位验证码" 
              size="large" 
              disabled={submitting} 
              className="flex-1"
              autoComplete="off"           // ✅ 禁用自动填充
              name="verification-code"     // ✅ 使用非标准 name 避免浏览器识别
            />
            <Button 
              type="primary" 
              size="large" 
              loading={sendingCode} 
              disabled={submitting || countdown > 0} 
              onClick={handleSendCode} 
              className="px-6 whitespace-nowrap"
            >
              {countdown > 0 ? `${countdown}s 后重发` : '获取验证码'}
            </Button>
          </div>
        </Form.Item>

        {/* 密码输入框 */}
        <Form.Item
          name="password"
          label={<span className="font-medium text-gray-700 flex items-center">登录密码<span className="text-red-500 ml-1">*</span></span>}
          rules={[{ required: true, message: '请输入密码' }]}
        >
          <Input.Password 
            placeholder="请输入登录密码" 
            size="large" 
            disabled={submitting}
            autoComplete="new-password"    // ✅ 使用 new-password 避免填充旧密码
            name="delete-password"         // ✅ 使用非标准 name
          />
        </Form.Item>

        {/* 确认注销输入框 */}
        <Form.Item
          name="confirm"
          label={<span className="font-medium text-gray-700 flex items-center">确认注销<span className="text-red-500 ml-1">*</span></span>}
          rules={[
            { required: true, message: '请输入 DELETE 确认注销' },
            { validator: (_, value) => value !== 'DELETE' ? Promise.reject('必须输入 DELETE') : Promise.resolve() },
          ]}
        >
          <Input 
            placeholder="输入 DELETE 确认注销" 
            size="large" 
            disabled={submitting} 
            className="text-red-500 font-mono"
            autoComplete="off"             // ✅ 禁用自动填充
            name="confirm-delete"          // ✅ 使用非标准 name
          />
        </Form.Item>

        {/* 提交按钮 */}
        <Form.Item className="mb-0 pt-4">
          <Button 
            type="primary" 
            danger 
            htmlType="submit" 
            size="large" 
            loading={submitting} 
            disabled={submitting} 
            className="w-full"
          >
            {submitting ? '注销中...' : '确认注销账号'}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};